/**
 * Lightweight, cross-browser alternative to Intl.DurationFormat for
 * hours + minutes durations. Mirrors the Intl API shape so it can be
 * swapped out once Intl.DurationFormat reaches "Widely Available" baseline
 * (expected Sept 2027).
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DurationFormat
 */

/**
 * Mirrors the duration input shape accepted by
 * Intl.DurationFormat.prototype.format() (hours + minutes subset).
 */
export type DurationInput = {
	hours?: number;
	minutes?: number;
};

/**
 * Display style matching Intl.DurationFormat style options.
 *
 * - `'long'`   → "2 hours 30 minutes"
 * - `'short'`  → "2 hr 30 min"
 * - `'narrow'` → "2h 30m"
 */
export type DurationStyle = 'long' | 'short' | 'narrow';

const UNIT_LABELS: Record<
	DurationStyle,
	{ hour: [string, string]; minute: [string, string] }
> = {
	long: { hour: ['hour', 'hours'], minute: ['minute', 'minutes'] },
	short: { hour: ['hr', 'hr'], minute: ['min', 'min'] },
	narrow: { hour: ['h', 'h'], minute: ['m', 'm'] },
};

/**
 * Formats a duration object into a human-readable string.
 *
 * @param duration - Object with optional `hours` and `minutes`
 * @param style    - `'long'` (default) | `'short'` | `'narrow'`
 * @returns Formatted duration string
 *
 * @example
 * formatDuration({ minutes: 30 })                          // "30 minutes"
 * formatDuration({ hours: 1 })                             // "1 hour"
 * formatDuration({ hours: 2 })                             // "2 hours"
 * formatDuration({ hours: 2, minutes: 30 })                // "2 hours 30 minutes"
 * formatDuration({ hours: 2, minutes: 30 }, 'short')       // "2 hr 30 min"
 * formatDuration({ hours: 2, minutes: 30 }, 'narrow')      // "2h 30m"
 */
export function formatDuration(
	duration: DurationInput,
	style: DurationStyle = 'long',
): string {
	const { hours = 0, minutes = 0 } = duration;
	const labels = UNIT_LABELS[style];
	const glue = style === 'narrow' ? '' : ' ';
	const parts: string[] = [];

	if (hours > 0) {
		parts.push(`${hours}${glue}${labels.hour[hours === 1 ? 0 : 1]}`);
	}

	if (minutes > 0 || parts.length === 0) {
		parts.push(`${minutes}${glue}${labels.minute[minutes === 1 ? 0 : 1]}`);
	}

	return parts.join(' ');
}

/**
 * Converts total minutes into a {@link DurationInput} object.
 * Useful for converting backend values (raw minutes) into the shape
 * expected by {@link formatDuration}.
 *
 * @param totalMinutes - Total duration in minutes
 * @returns `{ hours, minutes }` breakdown
 *
 * @example
 * minutesToDuration(30)  // { hours: 0, minutes: 30 }
 * minutesToDuration(150) // { hours: 2, minutes: 30 }
 */
export function minutesToDuration(totalMinutes: number): DurationInput {
	return {
		hours: Math.floor(totalMinutes / 60),
		minutes: totalMinutes % 60,
	};
}
